import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Shield, Clock, Users, Award, Truck, MapPin, Phone, CheckCircle, Mail } from 'lucide-react';
import { contactInfo } from '../mock';

export const About = () => {
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  const scrollToContact = () => {
    // Navigate to home page and scroll to contact section
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  };

  const companyValues = [
    {
      icon: Shield,
      title: "Reliability",
      description: "Your deliveries are safe with us. We take full responsibility for every package."
    },
    {
      icon: Clock,
      title: "Speed",
      description: "Time-critical deliveries are our specialty. We understand urgency in business."
    },
    {
      icon: Users,
      title: "Service",
      description: "Professional, friendly service from pickup to delivery. Your satisfaction is our priority."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "98% on-time delivery rate speaks for our commitment to exceptional service."
    }
  ];

  const whyChooseUs = [
    "Experienced drivers with extensive UK knowledge",
    "Modern fleet of vehicles for all delivery sizes",
    "Real-time tracking and SMS notifications",
    "Fully insured and secure handling",
    "Competitive pricing with transparent quotes",
    "24/7 customer support availability",
    "Established network across major UK cities",
    "Flexible scheduling to meet your needs"
  ];

  return (
    <section id="about" className="relative py-20 bg-gradient-to-b from-white to-slate-50">
      {/* Background Van Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-8"
        style={{
          backgroundImage: 'url("https://images.pexels.com/photos/4391470/pexels-photo-4391470.jpeg")'
        }}
      />
      
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-slate-50/85 to-emerald-50/80" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100/90 backdrop-blur-sm text-emerald-800 text-sm font-medium mb-6 shadow-lg">
            <Truck className="h-4 w-4 mr-2" />
            <span>About AIS Express</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 drop-shadow-sm">
            Your Trusted Delivery Partner
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            At AIS Express, we specialize in providing fast, reliable same-day delivery services 
            across the United Kingdom. Our mission is to connect businesses with their customers 
            through exceptional logistics solutions.
          </p>
        </div>

        {/* Company Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Our Story</h3>
            <div className="prose prose-lg text-slate-600 leading-relaxed">
              <p className="mb-4">
                Founded in <strong>August 2020</strong> with a vision to revolutionize same-day delivery services in the UK, 
                AIS Express has grown from a small local operation to a trusted partner for 
                businesses across the nation.
              </p>
              <p className="mb-4">
                We understand that in today's fast-paced business environment, every hour counts. 
                Whether you're a healthcare provider needing urgent medical supplies, a construction 
                company requiring immediate parts, or an e-commerce business fulfilling customer orders, 
                we deliver when it matters most.
              </p>
              <p>
                With over 15,000 successful deliveries and a 98% on-time rate, we've built our 
                reputation on reliability, speed, and exceptional customer service.
              </p>
            </div>
          </div>
          
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">What We Do</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 mt-1">
                  <MapPin className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Berkshire & Hampshire Coverage</h4>
                  <p className="text-slate-600">Based in Berkshire, we serve Reading, Slough, Wokingham, Bracknell, Windsor, Maidenhead, Basingstoke, Farnborough and extend nationwide for larger deliveries.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 mt-1">
                  <Truck className="h-4 w-4 text-teal-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Fleet Variety</h4>
                  <p className="text-slate-600">From small vans to 7.5-tonne lorries, we have the right vehicle for any delivery size.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 mt-1">
                  <Clock className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Time-Critical Solutions</h4>
                  <p className="text-slate-600">Specializing in urgent, same-day, and express deliveries when time is of the essence.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Values */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-slate-900 text-center mb-12">Our Core Values</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {companyValues.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center p-6 border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white group">
                  <CardContent className="p-0">
                    <div className="flex justify-center mb-4">
                      <div className={`flex h-16 w-16 items-center justify-center rounded-full ${
                        index % 2 === 0 ? 'bg-emerald-100' : 'bg-teal-100'
                      } group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className={`h-8 w-8 ${
                          index % 2 === 0 ? 'text-emerald-600' : 'text-teal-600'
                        }`} />
                      </div>
                    </div>
                    <h4 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h4>
                    <p className="text-slate-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Why Choose AIS Express?</h3>
              <div className="grid grid-cols-1 gap-3">
                {whyChooseUs.map((reason, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{reason}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-600 to-teal-600 text-white p-8 rounded-2xl">
              <h3 className="text-2xl font-bold mb-4">Ready to Experience the Difference?</h3>
              <p className="text-emerald-100 mb-6 leading-relaxed">
                Join over 100+ satisfied business partners who trust AIS Express for their 
                delivery needs. Get your instant quote today and discover why we're the 
                preferred choice for same-day delivery across the UK.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-emerald-200" />
                  <div>
                    <div className="font-semibold">Call for Instant Quote</div>
                    <div className="text-emerald-100">{contactInfo.phone}</div>
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-white text-emerald-600 hover:bg-emerald-50 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl mt-6"
                  onClick={scrollToContact}
                >
                  Get Started Today
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Company Stats */}
        <div className="text-center bg-white rounded-2xl p-12 shadow-xl border border-emerald-100 mb-16">
          <h3 className="text-2xl font-bold text-slate-900 mb-8">AIS Express by the Numbers</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="text-3xl font-bold text-emerald-600 mb-2">2020</div>
              <div className="text-slate-600 font-medium">Year Established</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-600 mb-2">15K+</div>
              <div className="text-slate-600 font-medium">Deliveries Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-600 mb-2">100+</div>
              <div className="text-slate-600 font-medium">Business Partners</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-600 mb-2">98%</div>
              <div className="text-slate-600 font-medium">Success Rate</div>
            </div>
          </div>
        </div>

        {/* Company Location & Coverage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Company Location */}
          <Card className="p-8 border-0 shadow-xl bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardContent className="p-0">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                  <MapPin className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Our Location</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">AIS Express Head Office</h4>
                  <div className="text-slate-700 leading-relaxed">
                    <div>22 Osmans Close</div>
                    <div>Bracknell, England</div>
                    <div>RG42 7PX</div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-emerald-200">
                  <h4 className="font-semibold text-slate-900 mb-2">Contact Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-emerald-600" />
                      <a href={`tel:${contactInfo.phone}`} className="text-emerald-600 hover:text-emerald-700 font-medium">
                        {contactInfo.phone}
                      </a>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-emerald-600" />
                      <a href={`mailto:${contactInfo.email}`} className="text-emerald-600 hover:text-emerald-700 font-medium">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Major Coverage Areas */}
          <Card className="p-8 border-0 shadow-xl bg-gradient-to-br from-teal-50 to-emerald-50">
            <CardContent className="p-0">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-100">
                  <Truck className="h-6 w-6 text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">Major Coverage Areas</h3>
              </div>
              
              <p className="text-slate-600 mb-6">
                We provide comprehensive same-day delivery services across these key locations in Berkshire and beyond:
              </p>
              
              <div className="grid grid-cols-2 gap-3 mb-6">
                {["Reading", "Slough", "Wokingham", "Bracknell", "Windsor", "Maidenhead", "Basingstoke", "Farnborough"].map((city, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-teal-500 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{city}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-white/60 p-4 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Extended Coverage:</strong> We also serve London, Birmingham, Manchester, 
                  and other major UK cities for larger commercial deliveries and specialized transport needs.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};